let fallbackId = 0;

export const TUTORIAL_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;

const TUTORIAL_EVENT_TYPES = new Set(['timer_started', 'timer_stopped', 'completed']);
const ELEMENT_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,120}$/;
const TASK_KEY_PATTERN = /^[A-Za-z0-9_.:-]{1,36}$/;
const POSITION_PLACEMENTS = new Set(['top', 'right', 'bottom', 'left', 'center']);
const MAX_TUTORIAL_NODES = 500;
const MAX_TUTORIAL_DEPTH = 20;
const MAX_PARTS_PER_SCENE = 100;

const createKey = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  fallbackId += 1;
  return `tutorial-node-${Date.now()}-${fallbackId}`;
};

export const createDefaultTutorialPart = () => ({
  message: '',
  navigate_to: '/user-home',
  target: null,
  advance_on_element_id: null,
  focus: { spotlight: true, zoom: 1.08 },
  layout: {
    responsive: true,
    reference_viewport: { width_px: null, height_px: null },
    minimum_scale: 0.35,
    maximum_scale: 1,
  },
  sprite: {
    anchor: 'target',
    placement: 'left',
    viewport: { x_percent: 50, y_percent: 50 },
    offset_px: { x: 0, y: 0 },
    rotation_deg: 0,
    facing: 'right',
    width_px: 96,
  },
  dialogue: {
    anchor: 'sprite',
    placement: 'right',
    viewport: { x_percent: 50, y_percent: 50 },
    offset_px: { x: 0, y: 0 },
    rotation_deg: 0,
    text_align: 'left',
    width_px: 558,
    height_px: 230,
    font_size_px: 13,
  },
  speech_tail: {
    tip_offset_px: { x: 0, y: 0 },
    base_offset_px: { x: 0, y: 0 },
    base_width_px: 52,
  },
  typing: {
    chars_per_second: 16,
    period_pause_multiplier: 4,
    play_every_n_letters: 1,
    sound_on_whitespace: false,
    sound_path: '/dopi_voice.wav',
    sound_gain: 1,
    blip_ms: 60,
    base_pitch_hz: 300,
    pitch_jitter_hz: 80,
  },
});

export const createDefaultTutorialScene = () => ({
  version: 2,
  parts: [createDefaultTutorialPart()],
  next_action: 'return_to_task',
});

export const createTutorialNode = ({
  key = createKey(),
  name = 'New tutorial step',
  durationSeconds = 60,
  children = [],
  scene = createDefaultTutorialScene(),
  eventType = 'timer_started',
} = {}) => ({
  key,
  name,
  durationSeconds,
  children,
  scene,
  eventType,
});

export const createTutorialTree = () => createTutorialNode({
  name: 'New Tutorial',
  durationSeconds: null,
  children: [createTutorialNode({ name: 'First tutorial step' })],
});

const mergeTutorialPart = (payload = {}) => {
  const defaults = createDefaultTutorialPart();
  return {
    ...defaults,
    ...payload,
    focus: { ...defaults.focus, ...(payload.focus || {}) },
    layout: {
      ...defaults.layout,
      ...(payload.layout || {}),
      reference_viewport: {
        ...defaults.layout.reference_viewport,
        ...(payload.layout?.reference_viewport || {}),
      },
    },
    sprite: {
      ...defaults.sprite,
      ...(payload.sprite || {}),
      viewport: { ...defaults.sprite.viewport, ...(payload.sprite?.viewport || {}) },
      offset_px: { ...defaults.sprite.offset_px, ...(payload.sprite?.offset_px || {}) },
    },
    dialogue: {
      ...defaults.dialogue,
      ...(payload.dialogue || {}),
      viewport: { ...defaults.dialogue.viewport, ...(payload.dialogue?.viewport || {}) },
      offset_px: { ...defaults.dialogue.offset_px, ...(payload.dialogue?.offset_px || {}) },
    },
    speech_tail: {
      ...defaults.speech_tail,
      ...(payload.speech_tail || {}),
      tip_offset_px: {
        ...defaults.speech_tail.tip_offset_px,
        ...(payload.speech_tail?.tip_offset_px || {}),
      },
      base_offset_px: {
        ...defaults.speech_tail.base_offset_px,
        ...(payload.speech_tail?.base_offset_px || {}),
      },
    },
    typing: { ...defaults.typing, ...(payload.typing || {}) },
  };
};

const mergeTutorialScene = (payload = {}) => {
  if (payload?.version === 2 && Array.isArray(payload.parts) && payload.parts.length) {
    return {
      version: 2,
      parts: payload.parts.map(mergeTutorialPart),
      next_action: payload.next_action === 'dismiss' ? 'dismiss' : 'return_to_task',
    };
  }
  return {
    version: 2,
    parts: [mergeTutorialPart(payload)],
    next_action: payload.next_action === 'dismiss' ? 'dismiss' : 'return_to_task',
  };
};

export const addTutorialPart = (scene, sourceIndex = 0) => {
  const normalized = mergeTutorialScene(scene);
  const source = normalized.parts[sourceIndex] || normalized.parts[0];
  const parts = [...normalized.parts];
  parts.splice(sourceIndex + 1, 0, JSON.parse(JSON.stringify(source)));
  return { ...normalized, parts };
};

export const moveTutorialPart = (scene, fromIndex, toIndex) => {
  const normalized = mergeTutorialScene(scene);
  if (
    fromIndex < 0 || fromIndex >= normalized.parts.length
    || toIndex < 0 || toIndex >= normalized.parts.length
    || fromIndex === toIndex
  ) return normalized;
  const parts = [...normalized.parts];
  const [part] = parts.splice(fromIndex, 1);
  parts.splice(toIndex, 0, part);
  return { ...normalized, parts };
};

export const removeTutorialPart = (scene, index) => {
  const normalized = mergeTutorialScene(scene);
  if (normalized.parts.length === 1) return normalized;
  return {
    ...normalized,
    parts: normalized.parts.filter((_part, partIndex) => partIndex !== index),
  };
};

export const tutorialTreeFromPackage = (tutorialPackage) => {
  if (
    tutorialPackage?.schema !== 'dopatize.tutorial-package'
    || tutorialPackage?.version !== 1
    || !tutorialPackage.task_tree
    || !Array.isArray(tutorialPackage.triggers)
  ) {
    throw new Error('This saved tutorial package is invalid.');
  }
  const triggerByTaskKey = new Map(
    tutorialPackage.triggers.map((trigger) => [trigger.task_key, trigger]),
  );

  const restoreNode = (node) => {
    if (!node || typeof node.key !== 'string' || typeof node.name !== 'string') {
      throw new Error('This saved tutorial contains an invalid task.');
    }
    const children = Array.isArray(node.children) ? node.children.map(restoreNode) : [];
    const trigger = triggerByTaskKey.get(node.key);
    return createTutorialNode({
      key: node.key,
      name: node.name,
      durationSeconds: node.duration_seconds ?? null,
      children,
      scene: mergeTutorialScene(trigger?.payload),
      eventType: trigger?.event_type || 'timer_started',
    });
  };

  return restoreNode(tutorialPackage.task_tree);
};

export const findTutorialNode = (node, key) => {
  if (node.key === key) return node;
  for (const child of node.children) {
    const match = findTutorialNode(child, key);
    if (match) return match;
  }
  return null;
};

export const updateTutorialNode = (node, key, update) => {
  if (node.key === key) return { ...node, ...update };
  return {
    ...node,
    children: node.children.map((child) => updateTutorialNode(child, key, update)),
  };
};

export const addTutorialChild = (node, parentKey, child = createTutorialNode()) => {
  if (node.key === parentKey) return { ...node, children: [...node.children, child] };
  return {
    ...node,
    children: node.children.map((item) => addTutorialChild(item, parentKey, child)),
  };
};

export const removeTutorialNode = (node, key) => ({
  ...node,
  children: node.children
    .filter((child) => child.key !== key)
    .map((child) => removeTutorialNode(child, key)),
});

export const flattenTutorialTree = (node, depth = 0, parentKey = null) => [
  { node, depth, parentKey },
  ...node.children.flatMap((child) => flattenTutorialTree(child, depth + 1, node.key)),
];

const validateInternalRoute = (route) => {
  if (route === null || route === undefined || route === '') return;
  if (
    typeof route !== 'string'
    || !route.startsWith('/')
    || route.startsWith('//')
    || route.includes('\\')
  ) {
    throw new Error('Tutorial navigation must be blank or an internal route beginning with /.');
  }
};

const serializePart = (part) => ({
  message: part.message.trim(),
  navigate_to: typeof part.navigate_to === 'string' && part.navigate_to.trim()
    ? part.navigate_to.trim()
    : null,
  target: part.target || null,
  advance_on_element_id: part.advance_on_element_id || null,
  focus: {
    spotlight: part.focus?.spotlight,
    zoom: part.focus?.zoom,
  },
  layout: {
    responsive: part.layout?.responsive !== false,
    minimum_scale: part.layout?.minimum_scale ?? 0.35,
    maximum_scale: part.layout?.maximum_scale ?? 1,
    reference_viewport: {
      width_px: part.layout?.reference_viewport?.width_px === null
        || part.layout?.reference_viewport?.width_px === ''
        || part.layout?.reference_viewport?.width_px === undefined
        ? 900
        : Number(part.layout.reference_viewport.width_px),
      height_px: part.layout?.reference_viewport?.height_px === null
        || part.layout?.reference_viewport?.height_px === ''
        || part.layout?.reference_viewport?.height_px === undefined
        ? 600
        : Number(part.layout.reference_viewport.height_px),
    },
  },
  sprite: {
    anchor: part.sprite?.anchor,
    placement: part.sprite?.placement,
    viewport: {
      x_percent: part.sprite?.viewport?.x_percent,
      y_percent: part.sprite?.viewport?.y_percent,
    },
    offset_px: {
      x: part.sprite?.offset_px?.x,
      y: part.sprite?.offset_px?.y,
    },
    rotation_deg: part.sprite?.rotation_deg,
    facing: part.sprite?.facing,
    width_px: part.sprite?.width_px,
  },
  dialogue: {
    anchor: part.dialogue?.anchor,
    placement: part.dialogue?.placement,
    viewport: {
      x_percent: part.dialogue?.viewport?.x_percent,
      y_percent: part.dialogue?.viewport?.y_percent,
    },
    offset_px: {
      x: part.dialogue?.offset_px?.x,
      y: part.dialogue?.offset_px?.y,
    },
    rotation_deg: part.dialogue?.rotation_deg,
    text_align: part.dialogue?.text_align,
    width_px: part.dialogue?.width_px,
    height_px: part.dialogue?.height_px,
    font_size_px: part.dialogue?.font_size_px,
  },
  speech_tail: {
    tip_offset_px: {
      x: part.speech_tail?.tip_offset_px?.x,
      y: part.speech_tail?.tip_offset_px?.y,
    },
    base_offset_px: {
      x: part.speech_tail?.base_offset_px?.x,
      y: part.speech_tail?.base_offset_px?.y,
    },
    base_width_px: part.speech_tail?.base_width_px,
  },
  typing: {
    chars_per_second: part.typing?.chars_per_second,
    period_pause_multiplier: part.typing?.period_pause_multiplier,
    play_every_n_letters: part.typing?.play_every_n_letters,
    sound_on_whitespace: part.typing?.sound_on_whitespace,
    sound_path: part.typing?.sound_path,
    sound_gain: part.typing?.sound_gain,
    blip_ms: part.typing?.blip_ms,
    base_pitch_hz: part.typing?.base_pitch_hz,
    pitch_jitter_hz: part.typing?.pitch_jitter_hz,
  },
});

const serializeScene = (scene) => {
  const normalized = mergeTutorialScene(scene);
  return {
    version: 2,
    parts: normalized.parts.map(serializePart),
    next_action: normalized.next_action,
  };
};

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const assertExactKeys = (value, expectedKeys, path) => {
  if (!isPlainObject(value)) throw new Error(`${path} must be a JSON object.`);
  const actualKeys = Object.keys(value);
  const unexpected = actualKeys.filter((key) => !expectedKeys.includes(key));
  const missing = expectedKeys.filter((key) => !Object.prototype.hasOwnProperty.call(value, key));
  if (unexpected.length) throw new Error(`${path} contains unsupported field “${unexpected[0]}”.`);
  if (missing.length) throw new Error(`${path} is missing required field “${missing[0]}”.`);
};

const assertString = (value, path, { minLength = 0, maxLength, pattern } = {}) => {
  if (
    typeof value !== 'string'
    || value.length < minLength
    || (maxLength !== undefined && value.length > maxLength)
    || (pattern && !pattern.test(value))
  ) {
    throw new Error(`${path} has an invalid text value.`);
  }
};

const assertNumber = (value, path, minimum, maximum, { integer = false } = {}) => {
  if (
    typeof value !== 'number'
    || !Number.isFinite(value)
    || value < minimum
    || value > maximum
    || (integer && !Number.isInteger(value))
  ) {
    throw new Error(`${path} must be ${integer ? 'a whole number' : 'a number'} from ${minimum} to ${maximum}.`);
  }
};

const assertBoolean = (value, path) => {
  if (typeof value !== 'boolean') throw new Error(`${path} must be true or false.`);
};

const validateElementId = (value, path) => {
  if (value !== null) assertString(value, path, { pattern: ELEMENT_ID_PATTERN });
};

const validateRoute = (value, path) => {
  if (value === null) return;
  assertString(value, path, { minLength: 1, maxLength: 2000 });
  try {
    validateInternalRoute(value);
  } catch (_error) {
    throw new Error(`${path} must be null or a safe internal application route.`);
  }
};

const validateViewport = (value, path) => {
  assertExactKeys(value, ['x_percent', 'y_percent'], path);
  assertNumber(value.x_percent, `${path}.x_percent`, 0, 100);
  assertNumber(value.y_percent, `${path}.y_percent`, 0, 100);
};

const validateOffset = (value, path) => {
  assertExactKeys(value, ['x', 'y'], path);
  assertNumber(value.x, `${path}.x`, -2000, 2000);
  assertNumber(value.y, `${path}.y`, -2000, 2000);
};

const validatePosition = (value, path, allowedAnchors) => {
  assertString(value.anchor, `${path}.anchor`);
  if (!allowedAnchors.has(value.anchor)) throw new Error(`${path}.anchor is unsupported.`);
  assertString(value.placement, `${path}.placement`);
  if (!POSITION_PLACEMENTS.has(value.placement)) throw new Error(`${path}.placement is unsupported.`);
  validateViewport(value.viewport, `${path}.viewport`);
  validateOffset(value.offset_px, `${path}.offset_px`);
  assertNumber(value.rotation_deg, `${path}.rotation_deg`, -360, 360);
};

const validateTutorialPart = (part, path) => {
  assertExactKeys(part, [
    'message', 'navigate_to', 'target', 'advance_on_element_id', 'focus', 'layout',
    'sprite', 'dialogue', 'speech_tail', 'typing',
  ], path);
  assertString(part.message, `${path}.message`, { minLength: 1, maxLength: 4000 });
  if (part.message !== part.message.trim()) {
    throw new Error(`${path}.message cannot begin or end with whitespace.`);
  }
  validateRoute(part.navigate_to, `${path}.navigate_to`);
  validateElementId(part.target, `${path}.target`);
  validateElementId(part.advance_on_element_id, `${path}.advance_on_element_id`);

  assertExactKeys(part.focus, ['spotlight', 'zoom'], `${path}.focus`);
  assertBoolean(part.focus.spotlight, `${path}.focus.spotlight`);
  assertNumber(part.focus.zoom, `${path}.focus.zoom`, 1, 1.25);

  assertExactKeys(
    part.layout,
    ['responsive', 'minimum_scale', 'maximum_scale', 'reference_viewport'],
    `${path}.layout`,
  );
  assertBoolean(part.layout.responsive, `${path}.layout.responsive`);
  assertNumber(part.layout.minimum_scale, `${path}.layout.minimum_scale`, 0.2, 1);
  assertNumber(part.layout.maximum_scale, `${path}.layout.maximum_scale`, part.layout.minimum_scale, 2);
  assertExactKeys(part.layout.reference_viewport, ['width_px', 'height_px'], `${path}.layout.reference_viewport`);
  assertNumber(part.layout.reference_viewport.width_px, `${path}.layout.reference_viewport.width_px`, 320, 3840);
  assertNumber(part.layout.reference_viewport.height_px, `${path}.layout.reference_viewport.height_px`, 240, 2160);

  assertExactKeys(
    part.sprite,
    ['anchor', 'placement', 'viewport', 'offset_px', 'rotation_deg', 'facing', 'width_px'],
    `${path}.sprite`,
  );
  validatePosition(part.sprite, `${path}.sprite`, new Set(['target', 'viewport']));
  if (!['left', 'right'].includes(part.sprite.facing)) throw new Error(`${path}.sprite.facing is unsupported.`);
  assertNumber(part.sprite.width_px, `${path}.sprite.width_px`, 48, 240);

  assertExactKeys(
    part.dialogue,
    ['anchor', 'placement', 'viewport', 'offset_px', 'rotation_deg', 'text_align', 'width_px', 'height_px', 'font_size_px'],
    `${path}.dialogue`,
  );
  validatePosition(part.dialogue, `${path}.dialogue`, new Set(['target', 'sprite', 'viewport']));
  if (!['left', 'center', 'right'].includes(part.dialogue.text_align)) {
    throw new Error(`${path}.dialogue.text_align is unsupported.`);
  }
  assertNumber(part.dialogue.width_px, `${path}.dialogue.width_px`, 240, 900);
  assertNumber(part.dialogue.height_px, `${path}.dialogue.height_px`, 140, 600);
  assertNumber(part.dialogue.font_size_px, `${path}.dialogue.font_size_px`, 8, 32);

  assertExactKeys(part.speech_tail, ['tip_offset_px', 'base_offset_px', 'base_width_px'], `${path}.speech_tail`);
  validateOffset(part.speech_tail.tip_offset_px, `${path}.speech_tail.tip_offset_px`);
  validateOffset(part.speech_tail.base_offset_px, `${path}.speech_tail.base_offset_px`);
  assertNumber(part.speech_tail.base_width_px, `${path}.speech_tail.base_width_px`, 16, 120);

  assertExactKeys(part.typing, [
    'chars_per_second', 'period_pause_multiplier', 'play_every_n_letters',
    'sound_on_whitespace', 'sound_path', 'sound_gain', 'blip_ms', 'base_pitch_hz',
    'pitch_jitter_hz',
  ], `${path}.typing`);
  assertNumber(part.typing.chars_per_second, `${path}.typing.chars_per_second`, 1, 60);
  assertNumber(part.typing.period_pause_multiplier, `${path}.typing.period_pause_multiplier`, 1, 10);
  assertNumber(part.typing.play_every_n_letters, `${path}.typing.play_every_n_letters`, 1, 20, { integer: true });
  assertBoolean(part.typing.sound_on_whitespace, `${path}.typing.sound_on_whitespace`);
  validateRoute(part.typing.sound_path, `${path}.typing.sound_path`);
  if (part.typing.sound_path === null) throw new Error(`${path}.typing.sound_path is required.`);
  assertNumber(part.typing.sound_gain, `${path}.typing.sound_gain`, 0, 2);
  assertNumber(part.typing.blip_ms, `${path}.typing.blip_ms`, 20, 250);
  assertNumber(part.typing.base_pitch_hz, `${path}.typing.base_pitch_hz`, 40, 2000);
  assertNumber(
    part.typing.pitch_jitter_hz,
    `${path}.typing.pitch_jitter_hz`,
    0,
    part.typing.base_pitch_hz * 0.95,
  );
};

const validateTutorialScene = (scene, path) => {
  assertExactKeys(scene, ['version', 'parts', 'next_action'], path);
  if (scene.version !== 2) throw new Error(`${path}.version must be 2.`);
  if (!Array.isArray(scene.parts) || !scene.parts.length || scene.parts.length > MAX_PARTS_PER_SCENE) {
    throw new Error(`${path}.parts must contain 1 to ${MAX_PARTS_PER_SCENE} tutorial parts.`);
  }
  scene.parts.forEach((part, index) => validateTutorialPart(part, `${path}.parts[${index}]`));
  if (!['return_to_task', 'dismiss'].includes(scene.next_action)) {
    throw new Error(`${path}.next_action is unsupported.`);
  }
};

export const validateTutorialPackageUpload = (tutorialPackage) => {
  assertExactKeys(
    tutorialPackage,
    ['schema', 'version', 'created_at', 'task_tree', 'triggers'],
    'Tutorial package',
  );
  if (tutorialPackage.schema !== 'dopatize.tutorial-package') {
    throw new Error('Tutorial package schema is not supported.');
  }
  if (tutorialPackage.version !== 1) throw new Error('Tutorial package version must be 1.');
  assertString(tutorialPackage.created_at, 'Tutorial package.created_at', { minLength: 1, maxLength: 64 });
  if (Number.isNaN(Date.parse(tutorialPackage.created_at))) {
    throw new Error('Tutorial package.created_at must be a valid date.');
  }

  const seenKeys = new Set();
  const leafKeys = [];
  let nodeCount = 0;
  const validateNode = (node, path, depth) => {
    if (depth > MAX_TUTORIAL_DEPTH) throw new Error(`Tutorial task tree exceeds ${MAX_TUTORIAL_DEPTH} levels.`);
    nodeCount += 1;
    if (nodeCount > MAX_TUTORIAL_NODES) throw new Error(`Tutorial task tree exceeds ${MAX_TUTORIAL_NODES} tasks.`);
    assertExactKeys(node, ['key', 'name', 'duration_seconds', 'children'], path);
    assertString(node.key, `${path}.key`, { pattern: TASK_KEY_PATTERN });
    if (seenKeys.has(node.key)) throw new Error(`Tutorial task key “${node.key}” is duplicated.`);
    seenKeys.add(node.key);
    assertString(node.name, `${path}.name`, { minLength: 1, maxLength: 200 });
    if (node.name !== node.name.trim()) throw new Error(`${path}.name cannot begin or end with whitespace.`);
    if (node.duration_seconds !== null) {
      assertNumber(node.duration_seconds, `${path}.duration_seconds`, 0, 2147483647, { integer: true });
    }
    if (!Array.isArray(node.children)) throw new Error(`${path}.children must be a list.`);
    if (!node.children.length) leafKeys.push(node.key);
    node.children.forEach((child, index) => validateNode(child, `${path}.children[${index}]`, depth + 1));
  };
  validateNode(tutorialPackage.task_tree, 'Tutorial package.task_tree', 1);

  if (!Array.isArray(tutorialPackage.triggers)) {
    throw new Error('Tutorial package.triggers must be a list.');
  }
  if (tutorialPackage.triggers.length !== leafKeys.length) {
    throw new Error('Tutorial package must contain exactly one trigger for every leaf task.');
  }
  const leafKeySet = new Set(leafKeys);
  const triggeredKeys = new Set();
  tutorialPackage.triggers.forEach((trigger, index) => {
    const path = `Tutorial package.triggers[${index}]`;
    assertExactKeys(trigger, ['task_key', 'event_type', 'extension_key', 'payload'], path);
    assertString(trigger.task_key, `${path}.task_key`, { pattern: TASK_KEY_PATTERN });
    if (!leafKeySet.has(trigger.task_key)) throw new Error(`${path}.task_key must reference a leaf task.`);
    if (triggeredKeys.has(trigger.task_key)) throw new Error(`Tutorial task “${trigger.task_key}” has more than one trigger.`);
    triggeredKeys.add(trigger.task_key);
    if (!TUTORIAL_EVENT_TYPES.has(trigger.event_type)) throw new Error(`${path}.event_type is unsupported.`);
    if (trigger.extension_key !== 'tutorial.show_scene') throw new Error(`${path}.extension_key is unsupported.`);
    validateTutorialScene(trigger.payload, `${path}.payload`);
  });
  return tutorialPackage;
};

export const parseTutorialPackageUpload = (jsonText) => {
  if (typeof jsonText !== 'string') throw new Error('The uploaded tutorial must be a JSON text file.');
  if (new Blob([jsonText]).size > TUTORIAL_UPLOAD_MAX_BYTES) {
    throw new Error('The tutorial JSON file must be 2 MB or smaller.');
  }
  let tutorialPackage;
  try {
    tutorialPackage = JSON.parse(jsonText);
  } catch (_error) {
    throw new Error('The uploaded file is not valid JSON.');
  }
  return validateTutorialPackageUpload(tutorialPackage);
};

export const buildTutorialExport = (root, createdAt = new Date().toISOString()) => {
  const flatNodes = flattenTutorialTree(root);
  flatNodes.forEach(({ node }) => {
    if (!node.name.trim()) throw new Error('Every task in the tutorial tree needs a name.');
    if (node.children.length) return;
    const scene = mergeTutorialScene(node.scene);
    scene.parts.forEach((part, index) => {
      if (!part.message.trim()) {
        throw new Error(`“${node.name}” part ${index + 1} needs tutorial dialogue text.`);
      }
      validateInternalRoute(part.navigate_to?.trim?.() ?? part.navigate_to);
    });
  });

  const serializeTree = (node) => ({
    key: node.key,
    name: node.name.trim(),
    duration_seconds: node.durationSeconds === null || node.durationSeconds === ''
      ? null
      : Number(node.durationSeconds),
    children: node.children.map(serializeTree),
  });

  const tutorialPackage = {
    schema: 'dopatize.tutorial-package',
    version: 1,
    created_at: createdAt,
    task_tree: serializeTree(root),
    triggers: flatNodes
      .filter(({ node }) => node.children.length === 0)
      .map(({ node }) => ({
        task_key: node.key,
        event_type: node.eventType,
        extension_key: 'tutorial.show_scene',
        payload: serializeScene(node.scene),
      })),
  };
  return validateTutorialPackageUpload(tutorialPackage);
};
